import { Box, Button, Icon, IconButton, Paper, Popper, Typography } from "@mui/material";
import { type ComponentProps, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { BackButton } from "~/components/Buttons";
import FernieMicro from "~/components/fernie/FernieMicro";
import FernieName from "~/components/fernie/FernieName";
import { anchorOwnsOpenPopper } from "~/components/walkthrough/anchorResolution";
import { WALKTHROUGH_Z_INDEX } from "~/components/walkthrough/walkthroughLayering";
import { fernieColors } from "~/themes/fernieBrand";
import { type WalkthroughStep } from "~/walkthroughs/types";

interface WalkthroughCoachmarkProps {
	anchorEl: HTMLElement;
	dismissible?: boolean;
	onBack: () => void;
	onNext: () => void;
	onSkip: () => void;
	step: WalkthroughStep;
	stepCount: number;
	stepIndex: number;
}

interface AnchorRect {
	height: number;
	left: number;
	top: number;
	width: number;
}

interface AnchorState {
	ownsOpenPopper: boolean;
	rect: AnchorRect;
}

const ANCHOR_GAP_PX = 14;

const COACHMARK_MODIFIERS: NonNullable<ComponentProps<typeof Popper>["modifiers"]> = [
	{ name: "offset", options: { offset: [0, ANCHOR_GAP_PX] } },
	{ name: "flip", options: { fallbackPlacements: ["top", "bottom", "left", "right"], padding: 8 } },
	// Popper's mainAxis is horizontal for top/bottom placements — keep it on so the card slides back onto a right-edge viewport instead of clipping.
	{ name: "preventOverflow", options: { altAxis: true, mainAxis: true, padding: 8, tether: false } }
];

const COACHMARK_POPPER_OPTIONS: NonNullable<ComponentProps<typeof Popper>["popperOptions"]> = { strategy: "fixed" };

const readRect = (element: HTMLElement): AnchorRect => {
	const box = element.getBoundingClientRect();
	return { height: box.height, left: box.left, top: box.top, width: box.width };
};

const readAnchorState = (element: HTMLElement): AnchorState => ({
	ownsOpenPopper: anchorOwnsOpenPopper(element),
	rect: readRect(element)
});

const useAnchorState = (anchorEl: HTMLElement): AnchorState => {
	const [state, setState] = useState<AnchorState>(() => readAnchorState(anchorEl));

	useLayoutEffect(() => {
		const update = () => setState(readAnchorState(anchorEl));
		update();
		window.addEventListener("scroll", update, true);
		window.addEventListener("resize", update);
		const interval = window.setInterval(update, 250);
		return () => {
			window.removeEventListener("scroll", update, true);
			window.removeEventListener("resize", update);
			window.clearInterval(interval);
		};
	}, [anchorEl]);

	return state;
};

const WalkthroughCoachmark = ({
	anchorEl,
	dismissible = false,
	onBack,
	onNext,
	onSkip,
	step,
	stepCount,
	stepIndex
}: WalkthroughCoachmarkProps) => {
	const { t } = useTranslation("common");
	const { ownsOpenPopper, rect } = useAnchorState(anchorEl);
	const isAction = step.gate.kind !== "manualNext";
	const canGoBack = stepIndex > 0;

	return (
		<>
			<Box
				data-testid="walkthrough-dim"
				sx={{
					borderRadius: "8px",
					boxShadow: `0 0 0 100vmax ${fernieColors.tile}80`,
					height: rect.height + 8,
					left: rect.left - 4,
					pointerEvents: "none",
					position: "fixed",
					top: rect.top - 4,
					transition: "top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease",
					width: rect.width + 8,
					zIndex: WALKTHROUGH_Z_INDEX.dim
				}}
			/>
			<Box
				data-testid="walkthrough-ring"
				sx={{
					"@keyframes fernieTourPulse": {
						"0%": { boxShadow: `0 0 0 2px ${fernieColors.fern}, 0 0 0 6px ${fernieColors.led}66` },
						"100%": { boxShadow: `0 0 0 2px ${fernieColors.fern}, 0 0 0 6px ${fernieColors.led}66` },
						"50%": { boxShadow: `0 0 0 2px ${fernieColors.fern}, 0 0 0 10px ${fernieColors.led}00` }
					},
					animation: "fernieTourPulse 1.6s ease-in-out infinite",
					borderRadius: "8px",
					height: rect.height + 8,
					left: rect.left - 4,
					pointerEvents: "none",
					position: "fixed",
					top: rect.top - 4,
					width: rect.width + 8,
					zIndex: WALKTHROUGH_Z_INDEX.ring
				}}
			/>
			{!ownsOpenPopper && (
				<Popper
					anchorEl={anchorEl}
					container={anchorEl.closest<HTMLElement>('[role="dialog"]')}
					modifiers={COACHMARK_MODIFIERS}
					open
					placement={step.placement ?? "right"}
					popperOptions={COACHMARK_POPPER_OPTIONS}
					sx={{ zIndex: WALKTHROUGH_Z_INDEX.card }}
				>
					<Paper
						data-testid="walkthrough-coachmark"
						elevation={8}
						sx={{
							border: `1px solid ${fernieColors.springSoft}`,
							borderRadius: "14px",
							maxWidth: 320,
							overflow: "hidden",
							p: 2
						}}
					>
						<Box sx={{ alignItems: "center", display: "flex", gap: 1, mb: 1 }}>
							<FernieMicro decorative size={22} />
							<FernieName color={fernieColors.richSoil} fontSize={15} />
							<Typography
								sx={{
									color: fernieColors.dim,
									fontSize: "0.68rem",
									fontWeight: 600,
									letterSpacing: "0.04em",
									ml: "auto",
									textTransform: "uppercase"
								}}
							>
								{t("walkthrough.stepCounter", { current: stepIndex + 1, total: stepCount })}
							</Typography>
							{dismissible && (
								<IconButton aria-label={t("actions.close")} onClick={onSkip} size="small">
									<Icon>close</Icon>
								</IconButton>
							)}
						</Box>

						<Typography sx={{ color: "text.primary", fontSize: "0.95rem", fontWeight: 700, mb: 0.5 }}>
							{t(step.titleKey)}
						</Typography>
						<Typography sx={{ color: "text.secondary", fontSize: "0.82rem", lineHeight: 1.5 }}>
							{t(step.bodyKey)}
						</Typography>

						{step.optionalNoteKey && (
							<Box
								data-testid="walkthrough-optional-note"
								sx={{ borderLeft: `2px solid ${fernieColors.springSoft}`, mt: 1.25, pl: 1 }}
							>
								<Typography
									component="span"
									sx={{
										color: fernieColors.fern,
										display: "block",
										fontSize: "0.62rem",
										fontWeight: 700,
										letterSpacing: "0.05em",
										textTransform: "uppercase"
									}}
								>
									{t("walkthrough.optional")}
								</Typography>
								<Typography sx={{ color: fernieColors.dim, fontSize: "0.75rem", lineHeight: 1.45 }}>
									{t(step.optionalNoteKey)}
								</Typography>
							</Box>
						)}

						{isAction && (
							<Box
								sx={{
									alignItems: "center",
									backgroundColor: `${fernieColors.led}33`,
									borderRadius: "8px",
									color: fernieColors.richSoil,
									display: "inline-flex",
									fontSize: "0.78rem",
									fontWeight: 600,
									gap: 0.5,
									mt: 1.5,
									px: 1,
									py: 0.5
								}}
							>
								{t("walkthrough.yourTurn")}
							</Box>
						)}

						<Box sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "flex-end", mt: 2 }}>
							<Button onClick={onSkip} size="small" sx={{ color: fernieColors.dim, mr: "auto", textTransform: "none" }}>
								{t("walkthrough.skip")}
							</Button>
							{canGoBack && (
								<BackButton
									onClick={onBack}
									size="small"
									sx={{ color: fernieColors.fern, textTransform: "none" }}
									variant="text"
								/>
							)}
							{step.gate.kind === "manualNext" && (
								<Button
									onClick={onNext}
									size="small"
									sx={{
										"&:hover": { backgroundColor: fernieColors.fern },
										backgroundColor: fernieColors.fern,
										borderRadius: "8px",
										color: "#FFFFFF",
										px: 2,
										textTransform: "none"
									}}
									variant="contained"
								>
									{t(stepIndex + 1 === stepCount ? "walkthrough.done" : "walkthrough.next")}
								</Button>
							)}
						</Box>
					</Paper>
				</Popper>
			)}
		</>
	);
};

export default WalkthroughCoachmark;
